import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { portalApi, refreshSession } from "./api";
import { codeOf } from "./errors";
import { remaining } from "./format";
import {
  CheckIcon,
  ChevronLeft,
  ChevronRight,
  ClockIcon,
  LockIcon,
} from "./icons";
import { useI18n, type Translate } from "./i18n";
import { genrePath } from "./routes";
import { usePortal } from "./state";
import type {
  CatalogueTitle,
  Genre,
  Lang,
  StoreRail,
  TitleSort,
  TitlesPage,
} from "./types";
import { Link, Notice, Poster, SkeletonCards } from "./ui";

/**
 * The store page's building blocks: a title tile, a lazy horizontal rail,
 * and a lazily paged grid. No carousel library - the portal keeps exactly
 * foyer-family's dependencies - so the mechanics are the platform's own:
 *
 * - **IntersectionObserver** decides WHEN to fetch: a rail reads its first
 *   page only once it comes within 200px of the viewport, and the next page
 *   when its last card comes within reach (rails: of the track's own edge;
 *   grids: of the viewport). Without IntersectionObserver (very old
 *   browsers) a rail loads at once and paging falls back to the buttons.
 * - **CSS scroll-snap** does the swiping on a phone; prev/next buttons and
 *   arrow keys (focus moves card to card, the browser scrolls it into view)
 *   do it on a keyboard. Images are `loading="lazy"` (`Poster`).
 */

export const STORE_PAGE_SIZE = 20;

export interface TitleParams {
  genre?: string;
  q?: string;
  sort?: TitleSort;
}

/**
 * One page of `/titles`. The catalogue routes are public with an OPTIONAL
 * session, so an expired access cookie reads as signed out; when the shell
 * believes the member is signed in, refresh once and re-read so a renter does
 * not see their own titles as locked.
 */
export async function readTitles(
  params: TitleParams & {
    page: number;
    pageSize?: number;
    lang: Lang;
  },
  signedIn: boolean,
  signal?: AbortSignal
): Promise<TitlesPage> {
  const opts = { pageSize: STORE_PAGE_SIZE, ...params };
  let res = await portalApi.titles(opts, signal);
  if (signedIn && !res.signedIn && (await refreshSession())) {
    res = await portalApi.titles(opts, signal);
  }
  return res;
}

export function isAbort(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

const hasObserver = () => typeof IntersectionObserver !== "undefined";

/** True once `ref` has come within `rootMargin` of the viewport. */
export function useOnceVisible(
  ref: RefObject<Element | null>,
  rootMargin = "200px"
): boolean {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;
    if (!hasObserver()) {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, seen]);
  return seen;
}

/**
 * Call `onNear` whenever `ref` comes within `rootMargin` of `root` (the
 * viewport when absent). Re-armed when `generation` changes, so a sentinel
 * still in view after a page lands asks for the next one.
 */
export function useWhenNear(
  ref: RefObject<Element | null>,
  onNear: () => void,
  opts: {
    root?: RefObject<Element | null>;
    rootMargin: string;
    enabled: boolean;
    generation: number;
  }
): void {
  const callback = useRef(onNear);
  callback.current = onNear;
  const { root, rootMargin, enabled, generation } = opts;
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || !hasObserver()) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) callback.current();
      },
      { root: root?.current ?? null, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, root, rootMargin, enabled, generation]);
}

/* ------------------------------------------------------------ the tile */

export function lengthLabel(seconds: number, t: Translate): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return t("card.minutes", { n: minutes });
  return t("card.hoursMinutes", {
    h: Math.floor(minutes / 60),
    m: minutes % 60,
  });
}

/** The server's clock for a page of titles: `serverTime` + when it landed. */
export interface ServerClock {
  serverTime: string;
  receivedAt: number;
}

/**
 * The tile's state badge, over the artwork:
 * - rented and running: "Utgår om 1 d 4 h", counted on the SERVER's clock
 *   (`serverTime` + time elapsed here), like Mitt innehåll;
 * - rented, not started / no limit: says so;
 * - otherwise a padlock: not yours to play (no prices - the shop is gone).
 */
function TileBadge({
  title,
  clock,
}: {
  title: CatalogueTitle;
  clock?: ServerClock;
}) {
  const { t } = useI18n();
  const rental = !title.locked ? title.rental : null;
  if (rental && rental.state !== "expired") {
    let text = t("title.rented");
    if (rental.state === "not_started") text = t("card.notStarted");
    else if (!rental.endsAt) text = t("card.noLimit");
    else if (clock) {
      const now =
        new Date(clock.serverTime).getTime() +
        Math.max(0, Date.now() - clock.receivedAt);
      const left = new Date(rental.endsAt).getTime() - now;
      text =
        left > 0
          ? t("card.expiresIn", { time: remaining(left, t) })
          : t("card.expired");
    }
    return (
      <span className="card__badge card__badge--rented">
        {rental.state === "active" && rental.endsAt ? (
          <ClockIcon className="card__badge-icon" />
        ) : (
          <CheckIcon className="card__badge-icon" />
        )}
        <span>{text}</span>
      </span>
    );
  }
  return (
    <span className="card__badge card__badge--muted">
      <LockIcon className="card__badge-icon" />
      <span>{t("title.locked")}</span>
    </span>
  );
}

export function TitleTile({
  title,
  clock,
}: {
  title: CatalogueTitle;
  clock?: ServerClock;
}) {
  const { t } = useI18n();
  const meta = [
    title.year ? String(title.year) : "",
    title.durationSeconds ? lengthLabel(title.durationSeconds, t) : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <li className="title-card">
      <Link
        to={`/titles/${encodeURIComponent(title.assetId)}`}
        className="card"
      >
        <span className="card__media">
          <Poster
            src={title.images.landscape ?? title.images.poster}
            title={title.title}
            shape="landscape"
          />
          <TileBadge title={title} clock={clock} />
        </span>
        <span className="title-card__name">{title.title}</span>
        {meta && <span className="title-card__meta">{meta}</span>}
      </Link>
    </li>
  );
}

/** ← / → move focus card to card inside a list of tiles. */
function arrowKeys(e: KeyboardEvent<HTMLUListElement>) {
  if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
  const links = Array.from(
    e.currentTarget.querySelectorAll<HTMLAnchorElement>(".title-card > a")
  );
  const at = links.indexOf(document.activeElement as HTMLAnchorElement);
  if (at < 0) return;
  const to = links[at + (e.key === "ArrowRight" ? 1 : -1)];
  if (to) {
    e.preventDefault();
    to.focus();
  }
}

/* ------------------------------------------------------------ the rail */

function reducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * One carousel: its heading and "see all" are there at once, its items are
 * fetched only when it nears the viewport, and its next page when the user
 * nears its end. An empty rail removes itself.
 */
export function Rail({ rail }: { rail: StoreRail }) {
  const { t, lang } = useI18n();
  const { me } = usePortal();
  const signedIn = !!me;
  const headId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const endRef = useRef<HTMLLIElement>(null);
  const visible = useOnceVisible(sectionRef);
  const [items, setItems] = useState<CatalogueTitle[] | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [clock, setClock] = useState<ServerClock | undefined>(undefined);
  const busy = useRef(false);
  const ctrl = useRef<AbortController | null>(null);
  const { genre, sort } = rail.query;

  const load = useCallback(
    async (page: number) => {
      if (page === 1) ctrl.current?.abort();
      else if (busy.current) return;
      const c = new AbortController();
      ctrl.current = c;
      busy.current = true;
      setFailed(false);
      try {
        const res = await readTitles(
          { genre, sort, page, lang },
          signedIn,
          c.signal
        );
        if (c.signal.aborted) return;
        setItems((prev) =>
          page === 1 ? res.titles : [...(prev ?? []), ...res.titles]
        );
        setNext(res.pagination.next);
        if (res.serverTime) {
          setClock({ serverTime: res.serverTime, receivedAt: Date.now() });
        }
      } catch (err) {
        if (!isAbort(err) && !c.signal.aborted) setFailed(true);
      } finally {
        if (ctrl.current === c) busy.current = false;
      }
    },
    [genre, sort, lang, signedIn]
  );

  useEffect(() => {
    if (visible) void load(1);
  }, [visible, load]);
  useEffect(() => () => ctrl.current?.abort(), []);

  useWhenNear(
    endRef,
    () => {
      if (next !== null) void load(next);
    },
    {
      root: trackRef,
      rootMargin: "0px 400px 0px 400px",
      enabled: items !== null && next !== null && !failed,
      generation: items?.length ?? 0,
    }
  );

  const scroll = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const left = dir * Math.max(200, track.clientWidth * 0.85);
    if (typeof track.scrollBy === "function") {
      track.scrollBy({ left, behavior: reducedMotion() ? "auto" : "smooth" });
    }
  };

  if (items !== null && items.length === 0 && !failed) return null;

  return (
    <section
      className="rail"
      ref={sectionRef}
      aria-labelledby={headId}
      data-rail={rail.id}
    >
      <div className="rail__head">
        <h2 id={headId} className="rail__title">
          {rail.label}
        </h2>
        <Link
          to={genrePath(rail.genreId ?? null)}
          className="rail__all"
          aria-label={t("store.rail.seeAllIn", { rail: rail.label })}
        >
          {t("store.rail.seeAll")}
        </Link>
        {items !== null && items.length > 1 && (
          <div className="rail__nav">
            <button
              type="button"
              className="rail__btn"
              onClick={() => scroll(-1)}
              aria-label={t("store.rail.prev", { rail: rail.label })}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className="rail__btn"
              onClick={() => scroll(1)}
              aria-label={t("store.rail.next", { rail: rail.label })}
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>
      {failed ? (
        <div className="rail__failed">
          <Notice kind="error">{t("store.rail.error")}</Notice>
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => void load(items === null ? 1 : (next ?? 1))}
          >
            {t("common.retry")}
          </button>
        </div>
      ) : items === null ? (
        // Skeleton cards the size of the real ones: the page does not jump
        // when the titles land.
        <ul className="rail__track rail__track--loading" aria-hidden="true">
          <SkeletonCards count={6} />
        </ul>
      ) : (
        <ul className="rail__track" ref={trackRef} onKeyDown={arrowKeys}>
          {items.map((title) => (
            <TitleTile key={title.assetId} title={title} clock={clock} />
          ))}
          {next !== null && (
            <li className="rail__end" ref={endRef} aria-hidden="true" />
          )}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------- a paged grid */

export interface PagedTitles {
  items: CatalogueTitle[];
  page: TitlesPage | null;
  loading: boolean;
  /** "catalogue_unavailable", another API code, or "error". */
  error: string | null;
  loadMore(): void;
  /** Read the first page again (after a failure). */
  reload(): void;
  genre: Genre | null | undefined;
  /** The server's clock for the rental countdowns on the tiles. */
  clock?: ServerClock;
}

/**
 * Every page of one `/titles` query, newest query wins: a changed query
 * aborts the request still in flight for the old one, so a slow answer to
 * "fl" can never overwrite the answer to "flykt". `params` null = idle.
 */
export function usePagedTitles(params: TitleParams | null): PagedTitles {
  const { lang } = useI18n();
  const { me } = usePortal();
  const signedIn = !!me;
  const [items, setItems] = useState<CatalogueTitle[]>([]);
  const [page, setPage] = useState<TitlesPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ctrl = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [clock, setClock] = useState<ServerClock | undefined>(undefined);
  const active = params !== null;
  const genre = params?.genre;
  const q = params?.q;
  const sort = params?.sort;

  useEffect(() => {
    ctrl.current?.abort();
    setItems([]);
    setPage(null);
    setError(null);
    if (!active) {
      setLoading(false);
      return;
    }
    const c = new AbortController();
    ctrl.current = c;
    busy.current = true;
    setLoading(true);
    readTitles({ genre, q, sort, page: 1, lang }, signedIn, c.signal)
      .then((res) => {
        if (c.signal.aborted) return;
        setItems(res.titles);
        setPage(res);
        if (res.serverTime) {
          setClock({ serverTime: res.serverTime, receivedAt: Date.now() });
        }
      })
      .catch((err: unknown) => {
        if (isAbort(err) || c.signal.aborted) return;
        setError(codeOf(err) || "error");
      })
      .finally(() => {
        if (ctrl.current === c) {
          busy.current = false;
          setLoading(false);
        }
      });
    return () => c.abort();
  }, [active, genre, q, sort, lang, signedIn, attempt]);

  const nextPage = page?.pagination.next ?? null;
  const loadMore = useCallback(() => {
    if (!active || nextPage === null || busy.current) return;
    const c = ctrl.current;
    if (!c) return;
    busy.current = true;
    setLoading(true);
    readTitles({ genre, q, sort, page: nextPage, lang }, signedIn, c.signal)
      .then((res) => {
        if (c.signal.aborted) return;
        setItems((prev) => [...prev, ...res.titles]);
        setPage(res);
      })
      .catch((err: unknown) => {
        if (isAbort(err) || c.signal.aborted) return;
        setError(codeOf(err) || "error");
      })
      .finally(() => {
        if (ctrl.current === c) {
          busy.current = false;
          setLoading(false);
        }
      });
  }, [active, nextPage, genre, q, sort, lang, signedIn]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return {
    items,
    page,
    loading,
    error,
    loadMore,
    reload,
    genre: page?.genre,
    clock,
  };
}

/** A grid of tiles that asks for the next page as its end nears. */
export function TitleGrid({
  paged,
  labelledBy,
}: {
  paged: PagedTitles;
  labelledBy?: string;
}) {
  const { t } = useI18n();
  const endRef = useRef<HTMLDivElement>(null);
  const next = paged.page?.pagination.next ?? null;
  useWhenNear(endRef, paged.loadMore, {
    rootMargin: "400px",
    enabled: next !== null && !paged.error,
    generation: paged.items.length,
  });
  return (
    <>
      <ul
        className="title-grid"
        aria-labelledby={labelledBy}
        onKeyDown={arrowKeys}
      >
        {paged.items.map((title) => (
          <TitleTile key={title.assetId} title={title} clock={paged.clock} />
        ))}
        {paged.items.length === 0 && paged.loading && (
          <SkeletonCards count={8} />
        )}
      </ul>
      <div ref={endRef} className="grid__end" aria-hidden="true" />
      {next !== null && (
        <button
          type="button"
          className="btn btn--quiet"
          onClick={paged.loadMore}
          disabled={paged.loading}
        >
          {paged.loading ? t("common.loading") : t("common.showMore")}
        </button>
      )}
    </>
  );
}
